import { NgClass } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { SvgIconComponent } from '@ngneat/svg-icon';
import moment from 'moment';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription, timer } from 'rxjs';

import { RemoveHtmlTagsPipe } from '../../../shared/pipes/removeHtmlTags/remove-html-tags.pipe';
import { InterventionFilterService } from '../../../shared/services/intervention-filter.service';
import { InterventionService } from '../../../shared/services/intervention.service';
import { AppConstant } from '../../../shared/utilities/app.constant';
import { AppHelper } from '../../../shared/utilities/app.helper';
import { getEventById } from '../../../shared/utilities/master-data.helper';

@Component({
  selector: 'app-nofitication-item-v2',
  templateUrl: './nofitication-item-v2.component.html',
  styleUrls: ['./nofitication-item-v2.component.scss'],
  imports: [NgClass, TooltipModule, RemoveHtmlTagsPipe, SvgIconComponent],
})
export class NofiticationItemV2Component
  implements OnInit, OnChanges, OnDestroy
{
  private _interventionLog: any;
  private _expirySubscription?: Subscription;

  @Input()
  get interventionLog(): any {
    return this._interventionLog;
  }

  set interventionLog(value: any) {
    this._interventionLog = value;
    this.updateViewModel();
  }

  @Input() selectedInterventionId: string = '';

  @Output()
  alertOpened = new EventEmitter<{ rigJournalId: string; alertId: string }>();

  eventHierarchy: string[] = [];
  shortTimeHHMM = 'HH:MM';
  statusClass: 'yellow-tag' | 'green-tag' | 'red-tag' | '' = 'yellow-tag';
  statusTextHover = 'Updating ...';

  constructor(
    @Inject(InterventionFilterService)
    private readonly _filterService: InterventionFilterService,
    interventionService: InterventionService,
    router: Router
  ) {
    this._interventionService = interventionService;
    this._router = router;
  }

  private readonly _interventionService: InterventionService;
  private readonly _router: Router;

  ngOnInit(): void {
    this.updateViewModel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['interventionLog']) {
      this.updateViewModel();
    }
  }

  ngOnDestroy(): void {
    this._expirySubscription?.unsubscribe();
  }

  private updateViewModel(): void {
    if (!this.interventionLog) {
      return;
    }

    this.statusClass = AppHelper.MathFunctions.getColorOfInterventionStatus(
      this.interventionLog.stateCode,
      this.interventionLog.timeExpire,
      this.interventionLog.timeSent
    );
    this.statusTextHover =
      AppHelper.MathFunctions.getActionOfInterventionStatus(
        this.interventionLog.stateCode,
        this.interventionLog.timeExpire,
        this.interventionLog.timeSent
      );

    if (
      this.interventionLog?.stateCode ===
      AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_DRAFT
    ) {
      this.statusClass = 'yellow-tag';
      this.statusTextHover = 'Initiated but not sent (draft)';
    }

    if (
      this.interventionLog?.stateCode ===
      AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_COMPLETED
    ) {
      if (this.requiresImmediateResolution) {
        this.statusClass = 'red-tag';
        this.statusTextHover =
          'Initiated and sent (requires immediate resolution)';
      } else {
        this.statusClass = 'green-tag';
        this.statusTextHover = 'Initiated and sent (awaiting resolution)';
      }
    }

    if (
      this.interventionLog?.stateCode ===
      AppConstant.INTERVENTION_STATE_CODE.RESOLUTION_ACTION_DRAFT
    ) {
      if (this.requiresImmediateResolution) {
        this.statusClass = 'yellow-tag';
        this.statusTextHover =
          'Resolution drafted but not sent (requires immediate resolution)';
      } else {
        this.statusClass = 'yellow-tag';
        this.statusTextHover = 'Resolution drafted but not sent';
      }
    }

    this.shortTimeHHMM = this.descriptionDate(
      this.interventionLog.createdAt || this.interventionLog.updatedAt
    );

    const eventInfo = getEventById(this.interventionLog?.event);
    this.eventHierarchy = eventInfo?.hierarchy
      ? eventInfo.hierarchy.split('/')
      : [eventInfo?.label || 'Updating...'];

    this.scheduleExpiryRefresh();
  }

  /**
   * The red-tag / "Requires Immediate Resolution" status depends on the
   * current wall-clock time vs. `timeExpire` (45 min SLA). Since this
   * component only recomputes its view model on `ngOnInit`/`ngOnChanges`,
   * it would otherwise never flip to the expired state until the parent
   * list happens to push a new `interventionLog` reference. Schedule a
   * one-shot timer to re-evaluate the status exactly when it expires.
   */
  private scheduleExpiryRefresh(): void {
    this._expirySubscription?.unsubscribe();
    this._expirySubscription = undefined;

    const timeExpire = AppHelper.MathFunctions.resolveTimeExpire(
      this.interventionLog?.timeExpire,
      this.interventionLog?.timeSent
    );
    if (!timeExpire) {
      return;
    }

    const msUntilExpiry = new Date(timeExpire).getTime() - Date.now();
    if (msUntilExpiry <= 0) {
      return;
    }

    this._expirySubscription = timer(msUntilExpiry).subscribe(() => {
      this.updateViewModel();
    });
  }

  get isUnreadIndicator(): boolean {
    return this._filterService.isUnread(this.interventionLog);
  }

  get isActionRequiredIndicator(): boolean {
    return this._filterService.isActionRequired(this.interventionLog);
  }

  private asBoolean(value: unknown): boolean {
    if (typeof value === 'string') {
      return value.trim().toLowerCase() === 'true';
    }

    return Boolean(value);
  }

  private get requiresImmediateResolution(): boolean {
    const stateCode = this.interventionLog?.stateCode;
    const timeExpire = AppHelper.MathFunctions.resolveTimeExpire(
      this.interventionLog?.timeExpire,
      this.interventionLog?.timeSent
    );
    const expired = timeExpire
      ? new Date(timeExpire).getTime() <= Date.now()
      : false;

    if (
      stateCode === AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_DRAFT
    ) {
      return false;
    }

    if (
      stateCode ===
      AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_COMPLETED
    ) {
      return expired;
    }

    if (
      stateCode === AppConstant.INTERVENTION_STATE_CODE.RESOLUTION_ACTION_DRAFT
    ) {
      return (
        expired ||
        this.requiresResolutionNotification ||
        this.asBoolean(this.interventionLog?.isRequiredFutherAction) ||
        this.asBoolean(this.interventionLog?.is_required_futher_action)
      );
    }

    return (
      this.isActionRequiredIndicator &&
      stateCode === AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_DRAFT
    );
  }

  get isCompletedStatus(): boolean {
    return this._filterService.isCompleted(this.interventionLog);
  }

  private get requiresResolutionNotification(): boolean {
    return (
      this.asBoolean(
        this.interventionLog?.isNotificationOfResolutionRequired
      ) ||
      this.asBoolean(
        this.interventionLog?.is_notification_of_resolution_required
      )
    );
  }

  get statusPillText(): string {
    if (this.isCompletedStatus) {
      return AppConstant.INTERVENTION_STATUS_TEXT.COMPLETED;
    }

    const stateCode = this.interventionLog?.stateCode;
    const hoverText = this.statusTextHover?.toLowerCase() ?? '';

    if (
      stateCode === AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_DRAFT
    ) {
      return AppConstant.INTERVENTION_STATUS_TEXT.DRAFT;
    }

    if (
      stateCode ===
        AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_COMPLETED &&
      this.requiresImmediateResolution
    ) {
      return AppConstant.INTERVENTION_STATUS_TEXT.REQUIRES_RESOLUTION;
    }

    if (
      stateCode ===
        AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_COMPLETED &&
      hoverText.includes('awaiting resolution')
    ) {
      return AppConstant.INTERVENTION_STATUS_TEXT.AWAITING_RESOLUTION;
    }

    if (
      stateCode === AppConstant.INTERVENTION_STATE_CODE.RESOLUTION_ACTION_DRAFT
    ) {
      return AppConstant.INTERVENTION_STATUS_TEXT.RESOLUTION_DRAFT;
    }

    if (
      stateCode ===
        AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_COMPLETED &&
      this.requiresImmediateResolution
    ) {
      return AppConstant.INTERVENTION_STATUS_TEXT.REQUIRES_RESOLUTION;
    }

    return AppConstant.INTERVENTION_STATUS_TEXT.DRAFT;
  }

  get statusPillClass(): string {
    if (this.isCompletedStatus) {
      return 'status-completed';
    }

    const stateCode = this.interventionLog?.stateCode;
    const hoverText = this.statusTextHover?.toLowerCase() ?? '';

    if (
      stateCode === AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_DRAFT
    ) {
      return 'status-drafted';
    }

    if (
      this.statusPillText ===
      AppConstant.INTERVENTION_STATUS_TEXT.AWAITING_RESOLUTION
    ) {
      return 'status-action-required';
    }

    if (
      stateCode === AppConstant.INTERVENTION_STATE_CODE.RESOLUTION_ACTION_DRAFT
    ) {
      return 'status-resolution-drafted';
    }

    if (
      hoverText.includes('requires immediate resolution') ||
      this.requiresImmediateResolution ||
      this.statusClass === 'red-tag' ||
      this.statusPillText ===
        AppConstant.INTERVENTION_STATUS_TEXT.REQUIRES_RESOLUTION
    ) {
      return 'status-action-required';
    }

    if (this.statusClass === 'green-tag') {
      return 'status-completed';
    }

    return 'status-drafted';
  }

  get statusIconKey(): string {
    if (this.isCompletedStatus) {
      return 'complete';
    }

    const stateCode = this.interventionLog?.stateCode;

    if (
      this.statusPillText ===
      AppConstant.INTERVENTION_STATUS_TEXT.AWAITING_RESOLUTION
    ) {
      return 'unReadEnvelope';
    }

    if (
      this.statusPillText ===
      AppConstant.INTERVENTION_STATUS_TEXT.REQUIRES_RESOLUTION
    ) {
      return 'unReadEnvelope';
    }

    if (
      stateCode === AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_DRAFT
    ) {
      return this.isUnreadIndicator || !this.interventionLog?.readStatus
        ? 'unReadEnvelope'
        : 'readEnvelope';
    }

    if (
      stateCode ===
        AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_COMPLETED &&
      this.requiresImmediateResolution
    ) {
      return 'unReadEnvelope';
    }

    if (
      [
        AppConstant.INTERVENTION_STATE_CODE.INITIATE_DRAFT,
        AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_DRAFT,
        AppConstant.INTERVENTION_STATE_CODE.RESOLUTION_ACTION_DRAFT,
      ].includes(stateCode)
    ) {
      return this.isUnreadIndicator || !this.interventionLog?.readStatus
        ? 'unReadEnvelope'
        : 'readEnvelope';
    }

    return this.isUnreadIndicator ? 'unReadEnvelope' : 'readEnvelope';
  }

  onCardClick(rigJournalId: string, alertId: string) {
    this.alertOpened.emit({ rigJournalId, alertId });

    this._interventionService.setInterventionLogsActive(alertId);

    this._router.navigate([`/home/rig/${rigJournalId}/alert/${alertId}`]);
  }

  collapseName(name: string): string {
    return name?.length > 20 ? `${name.slice(0, 20)}...` : name;
  }

  private descriptionDate(date: string): string {
    if (!date || !moment(date).isValid()) {
      return this.shortTimeHHMM;
    }

    const createdDate = moment(date);
    const now = moment();
    const hoursSinceCreated = now.diff(createdDate, 'hours', true);
    const timePart = AppHelper.DateFunctions.formatDateTime(
      createdDate.toDate(),
      true
    )
      .split(',')[1]
      ?.trim();

    if (hoursSinceCreated <= 24) {
      return `${timePart || '00:00'}, Today`;
    }

    if (hoursSinceCreated < 48) {
      return `${timePart || '00:00'}, Yesterday`;
    }

    return `${timePart || '00:00'}, ${AppHelper.DateFunctions.formatDateTime(
      createdDate.toDate(),
      false
    )}`;
  }
}
